// 🦷 치과에 공정 공개 스위치 (2026-08-17 대표 승인 — docs/기획서_치과공정공개_재설계_2026-08.md §5)
// 기공소 대표가 켜면, 연결된 치과가 자기 몫의 수집 케이스 진행(환자명·공정 단계·시각)을
// 제작 현황에서 볼 수 있다. 치과는 문서를 직접 읽지 않고 서버(CF)가 화이트리스트 필드만 응답한다.
// 켜는 순간(clinicProgressSince) 이후 수집분만 공개 — 과거 케이스는 열리지 않는다.
// 2026-09-02 대표 지시: 기본 켜짐 — 값이 없으면 켜진 것으로 보고(서버 CF 동일), 시작 시점은 2026-09-02(시행일). 끄면 false 저장.
// 2026-09-04 대표 확정(B안): 「치과에 디자인 미리보기 공개」 스위치 동거 — users.designPreviewEnabled(없음/true=켜짐, 끄면 false).
//   켜져 있으면 기공소 PC 덴클 프로그램이 디자인 완료 3D 파일(축소본)을 올리고 치과가 웹에서 미리 본다. 완료 처리 시 서버가 삭제.
// 2026-10-07 대표 결정: 「예상 기공비 표시」 섹션 동거 — 치과별 스위치(labShareSettings/{labUid}.expectedCost[clinicUid].enabled=false 만 기록, 없음=켜짐).
//   토글은 서버 callable setExpectedCostShare(기공소 대표만·연결 치과만). 기획 docs/기획서_치과_예상기공비_2026-10-07.md §3-2 · 법무 §4-3.
import React, { useEffect, useState } from 'react';
import { doc, onSnapshot, updateDoc, serverTimestamp, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { Eye, EyeOff } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { getFeatures, getClinicExpectedStatementOpenAtText } from '../../utils/features';   // 「기간 예상 기공료」 오픈 예정일 표시 (2026-10-07)

const CLINIC_TYPES = ['clinic', 'dental', '치과'];

function ClinicProgressShare({ user }) {
    const uid = user?.uid;
    const isStaff = user?.userType === 'staff';
    const businessType = user?.businessType || user?.companyBusinessType;
    const isLab = businessType === 'lab' || businessType === '기공소';

    const [me, setMe] = useState(undefined);   // undefined=로딩
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [busyPreview, setBusyPreview] = useState(false);
    const [previewNotice, setPreviewNotice] = useState(null);

    useEffect(() => {
        if (!uid) return;
        const unsub = onSnapshot(doc(db, 'users', uid), (snap) => setMe(snap.exists() ? snap.data() : null));
        return () => unsub();
    }, [uid]);

    // ── 예상 기공비·예상 명세서 표시 (2026-10-07) ──
    // 오픈 예정일(config/features.clinicExpectedStatementOpenAt, 없으면 "곧") — 문구 치환 전용. 훅은 early return 위
    const [features, setFeatures] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatures(f); });
        return () => { alive = false; };
    }, [uid]);
    const expStmtOpenAt = getClinicExpectedStatementOpenAtText(features);
    const [clinics, setClinics] = useState(undefined);        // undefined=로딩, [] = 연결 치과 없음. [{id, name}]
    const [clinicsError, setClinicsError] = useState('');
    const [shareDoc, setShareDoc] = useState(null);           // labShareSettings/{uid} (없음 = 전부 켜짐)
    const [shareDocError, setShareDocError] = useState('');
    const [costBusy, setCostBusy] = useState(null);           // 처리 중인 clinicUid
    const [costNotice, setCostNotice] = useState(null);

    // 연결(accepted) 회원 치과 목록 — requesterId/receiverId/recipientId 3방향, where 필터 필수. 치과 businessType 만.
    useEffect(() => {
        if (!uid || isStaff || !isLab) return;
        let alive = true;
        (async () => {
            try {
                const ref = collection(db, 'connections');
                const [s1, s2, s3] = await Promise.all([
                    getDocs(query(ref, where('requesterId', '==', uid), where('status', '==', 'accepted'))),
                    getDocs(query(ref, where('receiverId', '==', uid), where('status', '==', 'accepted'))),
                    getDocs(query(ref, where('recipientId', '==', uid), where('status', '==', 'accepted'))),
                ]);
                const ids = new Set();
                [...s1.docs, ...s2.docs, ...s3.docs].forEach(d => {
                    const c = d.data();
                    const pid = c.requesterId === uid ? (c.receiverId || c.recipientId) : c.requesterId;
                    if (pid && pid !== uid) ids.add(pid);
                });
                const out = [];
                for (const pid of ids) {
                    try {
                        const u = await getDoc(doc(db, 'users', pid));
                        if (!u.exists()) continue;
                        const d = u.data() || {};
                        if (!CLINIC_TYPES.includes(d.businessType || '')) continue;
                        out.push({ id: pid, name: d.businessName || d.companyName || d.name || '이름 없는 치과' });
                    } catch (e) {
                        console.warn('[예상기공비 설정] 치과 정보 조회 실패:', pid, e?.message);
                    }
                }
                out.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
                if (alive) setClinics(out);
            } catch (e) {
                console.error('[예상기공비 설정] 연결 치과 조회 실패:', e);
                if (alive) { setClinics([]); setClinicsError(`연결 치과 목록을 불러오지 못했습니다: ${e?.message || e}`); }
            }
        })();
        return () => { alive = false; };
    }, [uid, isStaff, isLab]);

    // labShareSettings/{uid} — rules: 기공소 대표 본인만 읽음. 문서 없음 = 전부 켜짐.
    useEffect(() => {
        if (!uid || isStaff || !isLab) return;
        const unsub = onSnapshot(doc(db, 'labShareSettings', uid),
            (snap) => { setShareDoc(snap.exists() ? snap.data() : {}); setShareDocError(''); },
            (e) => { console.error('[예상기공비 설정] 공개 설정 읽기 실패:', e); setShareDocError(`공개 설정을 불러오지 못했습니다: ${e?.message || e}`); });
        return () => unsub();
    }, [uid, isStaff, isLab]);

    const isCostShared = (clinicUid) => {
        const e = shareDoc?.expectedCost?.[clinicUid];
        return !(e && e.enabled === false);
    };
    const offSinceText = (clinicUid) => {
        const since = shareDoc?.expectedCost?.[clinicUid]?.since;
        if (!since) return '';
        const d = typeof since.toDate === 'function' ? since.toDate() : new Date(since);
        return isNaN(d) ? '' : `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
    };

    const toggleCost = async (clinic) => {
        if (costBusy) return;
        const on = isCostShared(clinic.id);
        // 끌 때는 확인창 없음(끄는 쪽이 안전한 방향). 되켤 때만 확인 — ☆ 문안 법무검토_치과_기간예상명세서 §1-4 글자 그대로 (2026-10-07 2차)
        if (!on && !window.confirm(
            `${clinic.name}에 예상 기공비·예상 명세서를 다시 보여줄까요?\n\n` +
            '· 보이는 것: 이번 달 예상 금액·건수·치아 수, 기간별 환자 이름·치식·보철 종류·개수·개당 단가·금액\n' +
            '· 안 보이는 것: 단가표 원본·다른 치과 정보·기공소 전체 매출\n' +
            '· 기공소 검토 없이 단가표로 자동 계산된 참고용입니다.'
        )) return;
        setCostBusy(clinic.id);
        setCostNotice(null);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'setExpectedCostShare');
            await fn({ clinicUid: clinic.id, enabled: !on });
            setCostNotice(on
                ? `${clinic.name}에 예상 기공비·예상 명세서 표시를 껐습니다 — 그 치과 화면에서 이 기공소 줄이 '표시하지 않음'으로 바뀝니다.`
                : `${clinic.name}에 예상 기공비·예상 명세서 표시를 켰습니다.`);
        } catch (e) {
            console.error('[예상기공비 설정] 변경 실패:', e);
            const code = e?.code || '';
            setCostNotice(code.includes('not-found')
                ? '예상 기공비 설정 기능이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.'
                : `설정 변경에 실패했습니다: ${e?.message || e}`);
        } finally {
            setCostBusy(null);
        }
    };

    if (isStaff || !isLab) {
        return (
            <div style={S.container}>
                <PageHeader icon={Eye} title="치과에 공정 공개" />
                <div style={S.emptyBox}>기공소 대표 계정만 사용할 수 있는 화면입니다.</div>
            </div>
        );
    }

    const enabled = me?.clinicProgressEnabled !== false;   // 미설정=켜짐(기본)
    const since = me?.clinicProgressSince;
    const sinceText = (() => {
        if (!since) return enabled ? '2026.9.2(기본 켜짐)' : '';
        const d = typeof since.toDate === 'function' ? since.toDate() : new Date(since);
        return isNaN(d) ? '' : `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    })();

    const previewEnabled = me?.designPreviewEnabled !== false;   // 미설정=켜짐(기본)
    const togglePreview = async () => {
        if (busyPreview || me === undefined) return;
        if (!previewEnabled && !window.confirm(
            '치과에 디자인 미리보기 공개를 켤까요?\n\n' +
            // 법무 §9-6 수정안 글자 그대로(10-15): 스캔 동봉·삭제 시점(포장 완료 7일 뒤, 최대 30일)·"그 의뢰서를 보낸 치과"
            '· 디자인이 끝난 케이스의 3D 파일(보철과 상·하악 스캔 등, 미리보기용 축소본)이 그 의뢰서를 보낸 치과에만 보입니다.\n' +
            '· 파일은 완료 처리 7일 뒤(늦어도 올린 날부터 30일) 자동 삭제됩니다.\n' +
            '· 금액·단가·작업 파일 원본은 보이지 않습니다.'
        )) return;
        setBusyPreview(true);
        try {
            await updateDoc(doc(db, 'users', uid), { designPreviewEnabled: !previewEnabled });
            setPreviewNotice(previewEnabled
                ? '디자인 미리보기 공개를 껐습니다 — 이후 완료되는 디자인은 치과에 올라가지 않습니다(이미 올라간 파일은 완료 처리 후 7일 뒤 삭제).'
                : '디자인 미리보기 공개를 켰습니다 — 이후 디자인이 끝나는 케이스부터 치과가 3D로 미리 볼 수 있습니다.');
        } catch (e) {
            console.error('디자인 미리보기 공개 설정 실패:', e);
            setPreviewNotice(`설정 변경에 실패했습니다: ${e?.message || e}`);
        } finally {
            setBusyPreview(false);
        }
    };

    const toggle = async () => {
        if (busy || me === undefined) return;
        if (!enabled && !window.confirm(
            '치과에 공정 공개를 켤까요?\n\n' +
            '· 연결된 치과가 자기 환자 케이스의 진행 단계(디자인 완료·완료)를 볼 수 있게 됩니다.\n' +
            '· 지금 이후에 수집되는 케이스만 보입니다 — 과거 케이스는 열리지 않습니다.\n' +
            '· 금액·단가·파일 이름은 절대 보이지 않습니다.'
        )) return;
        setBusy(true);
        try {
            await updateDoc(doc(db, 'users', uid), enabled
                ? { clinicProgressEnabled: false }
                : { clinicProgressEnabled: true, clinicProgressSince: serverTimestamp() });
            setNotice(enabled
                ? '공정 공개를 껐습니다 — 치과 화면에서 수집 케이스 진행이 더 이상 보이지 않습니다.'
                : '공정 공개를 켰습니다 — 지금부터 수집되는 케이스의 진행이 거래 치과에 보입니다.');
        } catch (e) {
            console.error('공정 공개 설정 실패:', e);
            setNotice(`설정 변경에 실패했습니다: ${e?.message || e}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={S.container}>
            <PageHeader icon={Eye} title="치과에 공정 공개" />
            <div style={S.desc}>
                켜면 <b>연결된 치과</b>가 자기 환자 케이스의 제작 진행(디자인 완료·완료)을
                치과 웹의 기공 현황에서 볼 수 있습니다. 보이는 것은 환자명·진행 단계·시각뿐이며,
                <b> 금액·단가·파일 이름은 보이지 않습니다.</b> 켠 시점 이후에 수집되는 케이스만 공개됩니다.
            </div>

            {notice && <div style={S.notice}>{notice}</div>}
            {me === undefined && <div style={S.emptyBox}>불러오는 중…</div>}

            {me !== undefined && (
                <div style={S.card}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {enabled ? <Eye size={18} color="#15803D" /> : <EyeOff size={18} color="#94a3b8" />}
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                            {enabled ? (me?.clinicProgressEnabled === true ? '공개 중' : '공개 중 (기본)') : '공개 안 함'}
                        </span>
                        {enabled && sinceText && (
                            <span style={{ fontSize: 12, color: '#64748b' }}>{sinceText}부터 수집된 케이스</span>
                        )}
                        <span style={{ flex: 1 }} />
                        <button onClick={toggle} disabled={busy}
                            style={{ ...(enabled ? S.offBtn : S.onBtn), ...(busy ? { opacity: 0.6 } : {}) }}>
                            {busy ? '처리 중…' : (enabled ? '공개 끄기' : '공개 켜기')}
                        </button>
                    </div>
                </div>
            )}

            <div style={S.note}>
                기본은 켜짐입니다(2026-09-02 이후 수집분). 언제든 끄고 켤 수 있습니다. 끄면 즉시 치과 화면에서 사라지고,
                다시 켜면 그 시점부터 새로 수집되는 케이스만 보입니다.
            </div>

            {/* 치과에 디자인 미리보기 공개 (2026-09-04 대표 확정 B안) */}
            <div style={{ ...S.sectionTitle, marginTop: 28 }}>치과에 디자인 미리보기 공개</div>
            <div style={S.desc}>
                {/* 법무 §9-6 수정안 글자 그대로(10-15) */}
                {'켜면 디자인이 끝난 케이스의 '}<b>{'3D 파일(보철·어버트먼트와 상·하악 스캔·모델의 미리보기용 축소본)'}</b>{'을 기공소 PC의 덴클 프로그램이 올리고, 그 의뢰서를 보낸 치과가 「오늘」 화면과 의뢰서 상세에서 회전·확대해 미리 볼 수 있습니다. '}
                <b>{'파일은 완료(포장 완료) 처리 7일 뒤 자동 삭제'}</b>{'되며(늦어도 올린 날부터 30일), 금액·단가·작업 원본 파일은 보이지 않습니다. 치과 화면에는 "미리보기는 디자인 확인용이며 최종 보철과 다를 수 있습니다" 안내가 함께 표시됩니다.'}
            </div>
            {previewNotice && <div style={S.notice}>{previewNotice}</div>}
            {me !== undefined && (
                <div style={S.card}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {previewEnabled ? <Eye size={18} color="#15803D" /> : <EyeOff size={18} color="#94a3b8" />}
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                            {previewEnabled ? (me?.designPreviewEnabled === true ? '공개 중' : '공개 중 (기본)') : '공개 안 함'}
                        </span>
                        <span style={{ flex: 1 }} />
                        <button onClick={togglePreview} disabled={busyPreview}
                            style={{ ...(previewEnabled ? S.offBtn : S.onBtn), ...(busyPreview ? { opacity: 0.6 } : {}) }}>
                            {busyPreview ? '처리 중…' : (previewEnabled ? '공개 끄기' : '공개 켜기')}
                        </button>
                    </div>
                </div>
            )}
            <div style={S.note}>
                기본은 켜짐입니다. 끄면 이후 완료되는 디자인은 올라가지 않습니다(이미 올라간 파일은 완료 처리 후 7일 뒤 삭제). 앱에서는 아직 볼 수 없습니다(웹만).
            </div>

            {/* 💰 예상 기공비·예상 명세서 표시 (2026-10-07 대표 결정: 기본 켜짐 · 치과별 끄기 · 스위치 하나로 둘 다) — ☆ 제목·설명 법무검토_치과_기간예상명세서 §1-4 글자 그대로. {오픈일}=config/features.clinicExpectedStatementOpenAt */}
            <div style={{ ...S.sectionTitle, marginTop: 28 }}>예상 기공비·예상 명세서 표시</div>
            <div style={S.desc}>
                {'거래처 치과가 「기공비 분석」에서 이번 달 예상 기공비(의뢰 건수·치아 수·예상 금액·명세서 확정 금액)를, '}
                {expStmtOpenAt ? `${expStmtOpenAt}부터는` : '곧'}
                {' 「받은 명세서」에서 기간별 예상 명세서(환자 이름·치식·보철 종류·개수·개당 단가·금액)를 봅니다. 이 기공소가 등록한 그 치과 단가표로 기공소 검토 없이 자동 계산되며, 다른 치과 단가와 기공소 전체 매출은 보이지 않습니다. 치과별로 끄고 켤 수 있고, 끄면 둘 다 꺼집니다.'}
            </div>
            {costNotice && <div style={S.notice}>{costNotice}</div>}
            {clinicsError && <div style={S.errBox}>{clinicsError}</div>}
            {shareDocError && <div style={S.errBox}>{shareDocError}</div>}
            {clinics === undefined && !clinicsError && <div style={S.emptyBox}>연결된 치과를 불러오는 중…</div>}
            {Array.isArray(clinics) && clinics.length === 0 && !clinicsError && (
                <div style={S.emptyBox}>연결된 회원 치과가 없습니다. 치과가 연결되면 여기에 치과별 스위치가 생깁니다(기본 켜짐).</div>
            )}
            {Array.isArray(clinics) && clinics.length > 0 && (
                <div style={S.card}>
                    {clinics.map((c, i) => {
                        const on = isCostShared(c.id);
                        const busy = costBusy === c.id;
                        const off = offSinceText(c.id);
                        return (
                            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                                {on ? <Eye size={16} color="#15803D" /> : <EyeOff size={16} color="#94a3b8" />}
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{c.name}</span>
                                <span style={{ fontSize: 12, color: on ? '#15803D' : '#64748b' }}>
                                    {on ? '표시 중 (기본)' : `표시 안 함${off ? ` · ${off}부터` : ''}`}
                                </span>
                                <span style={{ flex: 1 }} />
                                <button onClick={() => toggleCost(c)} disabled={!!costBusy || shareDoc === null}
                                    style={{ ...(on ? S.offBtn : S.onBtn), ...(busy || shareDoc === null ? { opacity: 0.6 } : {}) }}>
                                    {busy ? '처리 중…' : (on ? '표시 끄기' : '표시 켜기')}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
            <div style={S.note}>
                기본은 켜짐입니다. 끄면 그 치과에는 예상치가 표시되지 않습니다 — 치과 화면에서 이 기공소 줄이 '표시하지 않음'으로 바뀝니다.
                새로 연결되는 치과는 켜진 상태로 시작합니다. 끄고 켠 기록은 남습니다.
            </div>
        </div>
    );
}

const S = {
    container: { maxWidth: 760, margin: '0 auto', padding: '0 16px 40px' },
    sectionTitle: { fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 6 },
    desc: { fontSize: 13.5, color: '#64748b', lineHeight: 1.7, marginTop: 4, marginBottom: 16 },
    notice: {
        background: '#EDFBF1', border: '1px solid #BBF7D0', color: '#15803D',
        borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12, lineHeight: 1.6,
    },
    emptyBox: {
        background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12,
        padding: '32px 20px', textAlign: 'center', fontSize: 13.5, color: '#64748b',
    },
    card: {
        background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: '16px 18px', marginBottom: 12,
    },
    onBtn: {
        background: '#059669', border: '1px solid #059669', color: '#fff',
        borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    },
    offBtn: {
        background: '#fff', border: '1px solid #e2e8f0', color: '#64748b',
        borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    },
    note: { fontSize: 12.5, color: '#94a3b8', lineHeight: 1.7 },
    errBox: {
        background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020',
        borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12, lineHeight: 1.6,
    },
};

export default ClinicProgressShare;
